import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  MegaphoneIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { utcToLocalInput, localInputToUTC } from '../utils/datetime';

// ── Status display ─────────────────────────────────────────────────────────────

const STATUS_META = {
  draft:     { label: 'Draft',     cls: 'bg-gray-100 text-gray-600' },
  scheduled: { label: 'Scheduled', cls: 'bg-yellow-100 text-yellow-700' },
  sending:   { label: 'Sending',   cls: 'bg-blue-100 text-blue-700' },
  sent:      { label: 'Sent',      cls: 'bg-green-100 text-green-700' },
  failed:    { label: 'Failed',    cls: 'bg-red-100 text-red-700' },
  canceled:  { label: 'Canceled',  cls: 'bg-gray-100 text-gray-400' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
};

// ── Constants ──────────────────────────────────────────────────────────────────

const BROADCAST_TYPES = [
  { value: 'promotion', label: 'Promotion' },
  { value: 'system',    label: 'System' },
];

const ROLES = [
  { value: 'all',     label: 'All' },
  { value: 'riders',  label: 'Riders' },
  { value: 'drivers', label: 'Drivers' },
  { value: 'teens',   label: 'Teens' },
];

const LANGUAGES = [
  { value: 'all', label: 'All languages' },
  { value: 'ar',  label: 'Arabic' },
  { value: 'en',  label: 'English' },
];

const EMPTY_FORM = {
  title: '',
  titleAr: '',
  body: '',
  bodyAr: '',
  type: 'promotion',
  audience: { role: 'all', language: 'all', activeOnly: true, promoOptInOnly: false },
  channels: { push: true, inApp: true },
  scheduledAt: '',
};

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// ── Page ───────────────────────────────────────────────────────────────────────

export default function Broadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // existing broadcast in panel
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'send'|'cancel'|'delete', broadcast }
  const [actionLoading, setActionLoading] = useState(false);
  const [audienceEst, setAudienceEst] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const panelOpen = creating || !!selected;
  // Only draft and scheduled broadcasts allow edits
  const isEditable = !selected || ['draft', 'scheduled'].includes(selected.status);

  // ── Data ────────────────────────────────────────────────────────────────────

  const fetchBroadcasts = useCallback(async () => {
    try {
      const res = await api.get('/admin/broadcasts');
      setBroadcasts(res.data?.data || []);
    } catch {
      toast.error('Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBroadcasts(); }, [fetchBroadcasts]);

  // ── Panel helpers ───────────────────────────────────────────────────────────

  const openCreate = () => {
    setSelected(null);
    setCreating(true);
    setForm(EMPTY_FORM);
    setAudienceEst(null);
  };

  const openEdit = (b) => {
    setCreating(false);
    setSelected(b);
    setAudienceEst(null);
    setForm({
      title:       b.title      || '',
      titleAr:     b.titleAr    || '',
      body:        b.body       || '',
      bodyAr:      b.bodyAr     || '',
      type:        b.type       || 'promotion',
      audience: {
        role:           b.audience?.role          || 'all',
        language:       b.audience?.language      || 'all',
        activeOnly:     b.audience?.activeOnly    !== false,
        promoOptInOnly: !!b.audience?.promoOptInOnly,
      },
      channels: {
        push:  b.channels?.push  !== false,
        inApp: b.channels?.inApp !== false,
      },
      scheduledAt: utcToLocalInput(b.scheduledAt),
    });
  };

  const closePanel = () => {
    setSelected(null);
    setCreating(false);
    setForm(EMPTY_FORM);
    setAudienceEst(null);
  };

  const setAudField = (key, val) =>
    setForm((f) => ({ ...f, audience: { ...f.audience, [key]: val } }));

  const setChanField = (key, val) =>
    setForm((f) => ({ ...f, channels: { ...f.channels, [key]: val } }));

  // ── Payload builder ─────────────────────────────────────────────────────────

  const buildPayload = () => ({
    title:       form.title.trim(),
    titleAr:     form.titleAr.trim() || undefined,
    body:        form.body.trim(),
    bodyAr:      form.bodyAr.trim()  || undefined,
    type:        form.type,
    audience:    form.audience,
    channels:    form.channels,
    scheduledAt: localInputToUTC(form.scheduledAt),
  });

  const validate = () => {
    if (!form.title.trim()) { toast.error('English title is required'); return false; }
    if (!form.body.trim())  { toast.error('English body is required');  return false; }
    if (!form.channels.push && !form.channels.inApp) {
      toast.error('Enable at least one channel (push or in-app)'); return false;
    }
    return true;
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (selected) {
        const res = await api.put(`/admin/broadcasts/${selected._id}`, buildPayload());
        toast.success('Broadcast saved');
        setSelected(res.data?.data || selected);
      } else {
        // New draft: stay in panel so admin can immediately click Send
        const res = await api.post('/admin/broadcasts', buildPayload());
        toast.success('Draft created — click Send Now or Schedule to deliver');
        setCreating(false);
        setSelected(res.data?.data);
      }
      fetchBroadcasts();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = () => {
    if (!selected) return;
    setConfirmAction({ type: 'send', broadcast: selected });
  };

  const handleCancel = () => {
    if (!selected) return;
    setConfirmAction({ type: 'cancel', broadcast: selected });
  };

  const handleDelete = () => {
    if (!selected) return;
    setConfirmAction({ type: 'delete', broadcast: selected });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, broadcast } = confirmAction;
    setActionLoading(true);
    try {
      if (type === 'send') {
        // Persist any unsaved form changes (scheduledAt may differ from DB)
        if (isEditable) {
          await api.put(`/admin/broadcasts/${broadcast._id}`, buildPayload());
        }
        await api.post(`/admin/broadcasts/${broadcast._id}/send`);
        toast.success(form.scheduledAt ? 'Broadcast scheduled' : 'Broadcast queued for delivery');
      } else if (type === 'cancel') {
        await api.post(`/admin/broadcasts/${broadcast._id}/cancel`);
        toast.success('Broadcast canceled');
      } else if (type === 'delete') {
        await api.delete(`/admin/broadcasts/${broadcast._id}`);
        toast.success('Broadcast deleted');
      }
      setConfirmAction(null);
      closePanel();
      fetchBroadcasts();
    } catch (e) {
      toast.error(e?.response?.data?.message || `Failed to ${type} broadcast`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEstimate = async () => {
    setEstimating(true);
    setAudienceEst(null);
    try {
      const res = await api.post('/admin/broadcasts/audience-preview', form.audience);
      setAudienceEst(res.data?.data);
    } catch {
      toast.error('Failed to estimate audience');
    } finally {
      setEstimating(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Broadcasts</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Send bilingual push and in-app messages to riders, drivers, or teens.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-1.5">
          <PlusIcon className="w-4 h-4" /> New Broadcast
        </button>
      </div>

      <div className="flex gap-4 items-start">
        {/* Broadcast list */}
        <div className="flex-1 card overflow-hidden p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No broadcasts yet. Compose your first announcement.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Audience</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Scheduled</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Delivery</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {broadcasts.map((b) => (
                  <tr
                    key={b._id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      selected?._id === b._id ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => openEdit(b)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">{b.title}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {b.audience?.role} / {b.audience?.language}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {fmtDateTime(b.scheduledAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {b.stats
                        ? `${b.stats.pushSent ?? 0} push · ${b.stats.inAppCreated ?? 0} in-app`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={b.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel */}
        {panelOpen && (
          <div className="card w-96 flex-shrink-0 space-y-4">
            {/* Panel header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
                {selected
                  ? <><PencilIcon className="w-4 h-4" /> Edit Broadcast</>
                  : <><MegaphoneIcon className="w-4 h-4" /> New Broadcast</>}
              </h3>
              <button
                onClick={closePanel}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Delivery stats (read-only block, sent/failed only) */}
            {selected?.stats && ['sent', 'sending', 'failed'].includes(selected.status) && (
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 space-y-0.5">
                <div>Audience resolved: {selected.stats.audienceCount ?? '—'}</div>
                <div>
                  Push: {selected.stats.pushSent ?? 0} sent ·{' '}
                  {selected.stats.pushFailed ?? 0} failed
                </div>
                <div>In-app created: {selected.stats.inAppCreated ?? 0}</div>
              </div>
            )}

            <div className="space-y-3">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  className="input text-sm"
                  value={form.type}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {BROADCAST_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* English content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  className="input text-sm"
                  placeholder="Your ride is here"
                  value={form.title}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body (EN) <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="input text-sm resize-none"
                  rows={2}
                  placeholder="Message body in English"
                  value={form.body}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                />
              </div>

              {/* Arabic content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (AR)</label>
                <input
                  className="input text-sm text-right"
                  dir="rtl"
                  placeholder="عنوان بالعربية"
                  value={form.titleAr}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, titleAr: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body (AR)</label>
                <textarea
                  className="input text-sm resize-none text-right"
                  dir="rtl"
                  rows={2}
                  placeholder="نص الرسالة بالعربية"
                  value={form.bodyAr}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, bodyAr: e.target.value }))}
                />
              </div>

              {/* Audience section */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Audience
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Role</label>
                    <select
                      className="input text-sm"
                      value={form.audience.role}
                      disabled={!isEditable}
                      onChange={(e) => setAudField('role', e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Language</label>
                    <select
                      className="input text-sm"
                      value={form.audience.language}
                      disabled={!isEditable}
                      onChange={(e) => setAudField('language', e.target.value)}
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.audience.activeOnly}
                      disabled={!isEditable}
                      onChange={(e) => setAudField('activeOnly', e.target.checked)}
                    />
                    Active accounts only
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.audience.promoOptInOnly}
                      disabled={!isEditable}
                      onChange={(e) => setAudField('promoOptInOnly', e.target.checked)}
                    />
                    Promo opt-in users only
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleEstimate}
                  disabled={estimating || !isEditable}
                  className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-40 transition-colors"
                >
                  <UsersIcon className="w-3.5 h-3.5" />
                  {estimating ? 'Estimating…' : 'Estimate audience'}
                </button>
                {audienceEst && (
                  <div className="mt-1 text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1">
                    ~{audienceEst.total ?? 0} recipients
                    ({audienceEst.userCount ?? 0} users · {audienceEst.driverCount ?? 0} drivers)
                  </div>
                )}
              </div>

              {/* Channels section */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Channels
                </div>
                <div className="flex gap-5">
                  <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.channels.push}
                      disabled={!isEditable}
                      onChange={(e) => setChanField('push', e.target.checked)}
                    />
                    Push
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.channels.inApp}
                      disabled={!isEditable}
                      onChange={(e) => setChanField('inApp', e.target.checked)}
                    />
                    In-app inbox
                  </label>
                </div>
              </div>

              {/* Schedule section */}
              <div className="border-t pt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule for (empty = send immediately)
                </label>
                <input
                  type="datetime-local"
                  className="input text-sm"
                  value={form.scheduledAt}
                  disabled={!isEditable}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-2 border-t">
              {/* Save draft / Save changes + Send */}
              {(creating || isEditable) && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || actionLoading}
                    className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving…' : selected ? 'Save Changes' : 'Save Draft'}
                  </button>
                  {selected && isEditable && (
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={saving || actionLoading}
                      className="btn-primary text-sm flex-1"
                    >
                      {form.scheduledAt ? 'Schedule' : 'Send Now'}
                    </button>
                  )}
                </div>
              )}

              {/* Read-only notice for terminal statuses */}
              {selected && !isEditable && (
                <p className="text-xs text-gray-400 text-center py-1">
                  Broadcast is {selected.status} — no edits allowed.
                </p>
              )}

              {/* Cancel schedule / Delete */}
              {selected && (
                <div className="flex gap-2">
                  {selected.status === 'scheduled' && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={actionLoading}
                      className="flex-1 text-sm px-3 py-2 border border-amber-300 rounded-lg text-amber-700 hover:bg-amber-50 font-medium disabled:opacity-50 transition-colors"
                    >
                      Cancel Schedule
                    </button>
                  )}
                  {['draft', 'scheduled'].includes(selected.status) && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={actionLoading}
                      className="btn-danger text-sm flex items-center gap-1.5"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmAction}
        title={
          confirmAction?.type === 'delete' ? 'Delete Broadcast'
          : confirmAction?.type === 'send' ? (form.scheduledAt ? 'Schedule Broadcast' : 'Send Broadcast')
          : 'Cancel Broadcast'
        }
        message={
          confirmAction?.type === 'delete'
            ? `Delete "${confirmAction?.broadcast?.title}"? This cannot be undone.`
          : confirmAction?.type === 'send'
            ? `${form.scheduledAt ? 'Schedule' : 'Send'} "${confirmAction?.broadcast?.title}" to the selected audience?`
          : `Cancel the scheduled broadcast "${confirmAction?.broadcast?.title}"?`
        }
        confirmLabel={
          confirmAction?.type === 'delete' ? 'Delete'
          : confirmAction?.type === 'send' ? (form.scheduledAt ? 'Schedule' : 'Send Now')
          : 'Cancel Broadcast'
        }
        variant={confirmAction?.type === 'delete' ? 'danger' : 'primary'}
        loading={actionLoading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
